import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Reviews.css";

const API_BASE_URL = 'https://malaikabackend.onrender.com/api';

function StarRating({ count }) {
  return (
    <div className="review-stars" aria-label={`${count} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= count ? "star filled" : "star"}>
          ★
        </span>
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    // Temporarily remove clamp to measure full height
    el.style.webkitLineClamp = "unset";
    el.style.display = "block";
    const fullHeight = el.scrollHeight;
    el.style.webkitLineClamp = "";
    el.style.display = "";
    const clampedHeight = el.clientHeight;
    setIsClamped(fullHeight > clampedHeight + 4);
  }, [review.reviewText]);

  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'A';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="review-card">
      <div className="review-profile-initial">
        {getInitial(review.name)}
      </div>
      <StarRating count={review.rating} />
      <p className="review-author">{review.name}</p>
      <p className="review-role">{formatDate(review.createdAt)}</p>
      <p className="review-service-type">{review.serviceType}</p>
      <p
        ref={textRef}
        className={`review-text${expanded ? "" : " collapsed"}`}
      >
        {review.reviewText}
      </p>
      {isClamped && (
        <button
          className="show-more-btn"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

export default function Reviews() {
  const [reviewsData, setReviewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const visible = 2;
  const total = reviewsData.length;
  const steps = Math.max(0, total - visible);

  const [current, setCurrent] = useState(0);
  const autoTimerRef = useRef(null);
  const trackRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/reviews`);
      const data = await response.json();
      
      if (data.success) {
        setReviewsData(data.data);
      } else {
        setError('Failed to fetch reviews');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const goTo = useCallback(
    (index) => {
      const n = Math.max(0, Math.min(index, steps));
      setCurrent(n);
    },
    [steps]
  );

  const resetAuto = useCallback(() => {
    clearInterval(autoTimerRef.current);
    if (total > visible) {
      autoTimerRef.current = setInterval(() => {
        setCurrent((prev) => (prev >= steps ? 0 : prev + 1));
      }, 4000);
    }
  }, [steps, total, visible]);

  useEffect(() => {
    resetAuto();
    return () => clearInterval(autoTimerRef.current);
  }, [resetAuto]);

  const handleMove = (dir) => {
    resetAuto();
    goTo(current + dir);
  };

  const handleDot = (i) => {
    resetAuto();
    goTo(i);
  };

  // Calculate translate based on card width
  const getTranslate = () => {
    if (!wrapperRef.current) return 0;
    const cardW = wrapperRef.current.offsetWidth / visible;
    return current * (cardW + 16);
  };

  if (loading) {
    return (
      <section className="reviews-section">
        <h2 className="header2" style={{textAlign: 'center'}}>Client Reviews</h2>
        <div style={{textAlign: 'center', padding: '40px'}}>
          <p>Loading reviews...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="reviews-section">
        <h2 className="header2" style={{textAlign: 'center'}}>Client Reviews</h2>
        <div style={{textAlign: 'center', padding: '40px'}}>
          <p style={{color: 'red'}}>Error: {error}</p>
        </div>
      </section>
    );
  }

  if (reviewsData.length === 0) {
    return (
      <section className="reviews-section">
        <h2 className="header2" style={{textAlign: 'center'}}>Client Reviews</h2>
        <div style={{textAlign: 'center', padding: '40px'}}>
          <p>No reviews available yet.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="reviews-section">
      <h2 className="header2" style={{textAlign: 'center'}}>Client Reviews</h2>
      <div className="carousel-wrapper" ref={wrapperRef}>
        <div
          className="carousel-track"
          ref={trackRef}
          style={{ transform: `translateX(-${getTranslate()}px)` }}
        >
          {reviewsData.map((review) => (
            <ReviewCard key={review._id} review={review} />
          ))}
        </div>
      </div>

      {total > visible && (
        <div className="carousel-controls">
          <button
            className="ctrl-btn"
            onClick={() => handleMove(-1)}
            disabled={current === 0}
            aria-label="Previous reviews"
          >
            &#8592;
          </button>

          <div className="dots" role="tablist" aria-label="Review pages">
            {Array.from({ length: steps + 1 }, (_, i) => (
              <button
                key={i}
                className={`dot${current === i ? " active" : ""}`}
                onClick={() => handleDot(i)}
                role="tab"
                aria-selected={current === i}
                aria-label={`Go to page ${i + 1}`}
              />
            ))}
          </div>

          <button
            className="ctrl-btn"
            onClick={() => handleMove(1)}
            disabled={current === steps}
            aria-label="Next reviews"
          >
            &#8594;
          </button>
        </div>
      )}
    </section>
  );
}